import { useState } from 'react'
import { ChevronDown, Plus, Trash2, Eye, EyeOff } from 'lucide-react'
import IndicatorConfigModal from '@/components/Indicators/IndicatorConfigModal'
import ColorPickerField from '@/components/Indicators/ColorPickerField'
import SliderField from '@/components/Indicators/SliderField'
import ToggleField from '@/components/Indicators/ToggleField'
import EmaSmaNotificationsSection from './NotificationsSection.jsx'
import { LineSwatch, LineStyleSelect, LineWidthSelect } from './LineControls.jsx'
import { resolveConfig, SAFE_DEFAULTS, DEFAULT_LINE_STYLE, DEFAULT_LINE_WIDTH } from './constants.js'

function Section({ title, open, onToggle, children }) {
  return (
    <div className="border border-[var(--hair,rgba(176,141,87,0.35))] rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-2.5 py-2 bg-[var(--paper,#FDFDFA)] hover:bg-black/[0.02] transition-colors"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--gold,#B08D57)]">{title}</span>
        <ChevronDown size={14} className={`text-[var(--ink-soft,#4A5A52)] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="px-2.5 py-2 space-y-2 bg-white/50 dark:bg-gray-900/20">{children}</div>}
    </div>
  )
}

export default function EmaSmaPanel({
  config, onChange, onClose, chartSymbol, chartTimeframe,
  addMovingAverage, removeMovingAverage, updateMovingAverage,
}) {
  const cfg = resolveConfig(config)
  const mas = cfg.mas
  const [activeSection, setActiveSection] = useState('mas')
  const toggleSection = (key) => setActiveSection(prev => prev === key ? '' : key)

  const update = (key, value) => onChange({ ...cfg, [key]: value })
  // Las mutaciones de medias van por las acciones REALES de useIndicatorState
  // (movingAverages, la fuente de verdad) cuando el caller las pasa — igual
  // que ya hace el badge. Sin esto, editar aquí escribía sólo en
  // trading_bot_indicator_configs y useEmaSmaSync lo revertía en el
  // siguiente ciclo con el movingAverages desincronizado (bug real, cazado
  // por el E2E de la Tarea B). Fallback a onChange('mas', ...) si no se
  // pasan (p.ej. tests unitarios del propio Panel sin esos props).
  const updateMa = (id, patch) => {
    if (updateMovingAverage) { updateMovingAverage(id, patch); return }
    update('mas', mas.map(ma => ma.id === id ? { ...ma, ...patch } : ma))
  }
  const addMa = (type) => {
    const period = type === 'sma' ? 50 : 20
    const color = type === 'sma' ? '#ef4444' : '#f59e0b'
    if (addMovingAverage) { addMovingAverage(type, { period, color }); return }
    update('mas', [...mas, { id: `ma-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, type, period, color, active: true, lineStyle: DEFAULT_LINE_STYLE, lineWidth: DEFAULT_LINE_WIDTH }])
  }
  const removeMa = (id) => {
    if (removeMovingAverage) { removeMovingAverage(id); return }
    update('mas', mas.filter(ma => ma.id !== id))
  }

  const resetAll = () => {
    if (typeof window !== 'undefined' && !window.confirm('¿Restablecer medias móviles a valores por defecto?')) return
    onChange(SAFE_DEFAULTS)
  }

  return (
    <IndicatorConfigModal title="Configuración EMA/SMA Quantum" isOpen onClose={onClose}>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
        <Section title="Medias móviles" open={activeSection === 'mas'} onToggle={() => toggleSection('mas')}>
          {mas.length === 0 && (
            <div className="text-center py-3 text-xs text-[#4A5A52] dark:text-slate-400">
              Sin medias móviles configuradas.
            </div>
          )}
          {mas.map(ma => (
            <div key={ma.id} data-testid={`ma-row-${ma.type}-${ma.period}`} className="py-1.5 space-y-1.5 border-b border-[var(--hair,rgba(176,141,87,0.2))] last:border-0">
              <div className="flex items-center gap-2">
                <ColorPickerField
                  label=""
                  value={ma.color}
                  onChange={(color) => updateMa(ma.id, { color })}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-medium shrink-0 whitespace-nowrap text-[#15211B] dark:text-slate-100">
                      {ma.type.toUpperCase()}
                    </span>
                    <SliderField
                      label=""
                      value={ma.period}
                      min={1}
                      max={500}
                      step={1}
                      onChange={(period) => updateMa(ma.id, { period })}
                    />
                  </div>
                </div>
                <LineSwatch color={ma.color} lineStyle={ma.lineStyle} lineWidth={ma.lineWidth} />
                <button
                  onClick={() => updateMa(ma.id, { active: !ma.active })}
                  className={`p-1.5 rounded transition-colors ${ma.active ? 'text-[#15211B] dark:text-slate-100' : 'text-[#4A5A52] dark:text-slate-400'}`}
                  title={ma.active ? 'Ocultar' : 'Mostrar'}
                >
                  {ma.active ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button
                  onClick={() => removeMa(ma.id)}
                  className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  style={{ color: '#ef4444' }}
                  title="Eliminar"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="flex items-center gap-2 pl-1">
                <div className="flex-1">
                  <LineStyleSelect compact value={ma.lineStyle ?? DEFAULT_LINE_STYLE} onChange={(lineStyle) => updateMa(ma.id, { lineStyle })} />
                </div>
                <div className="flex-1">
                  <LineWidthSelect compact value={ma.lineWidth ?? DEFAULT_LINE_WIDTH} onChange={(lineWidth) => updateMa(ma.id, { lineWidth })} />
                </div>
              </div>
            </div>
          ))}
        </Section>

        <Section title="Añadir media" open={activeSection === 'add'} onToggle={() => toggleSection('add')}>
          <div className="flex gap-2">
            <button
              onClick={() => addMa('ema')}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-[var(--paper,#FDFDFA)] text-[var(--green-deep,#0B3D2E)] hover:bg-[var(--gold,#B08D57)] hover:text-white transition-colors"
            >
              <Plus size={14} /> Añadir EMA
            </button>
            <button
              onClick={() => addMa('sma')}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-[var(--paper,#FDFDFA)] text-[var(--green-deep,#0B3D2E)] hover:bg-[var(--gold,#B08D57)] hover:text-white transition-colors"
            >
              <Plus size={14} /> Añadir SMA
            </button>
          </div>
        </Section>

        <Section title="Notificaciones" open={activeSection === 'notifications'} onToggle={() => toggleSection('notifications')}>
          <EmaSmaNotificationsSection config={cfg} onChange={onChange} />
        </Section>

        <button
          onClick={resetAll}
          className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-[var(--paper,#FDFDFA)] text-[var(--green-deep,#0B3D2E)] hover:bg-[var(--gold,#B08D57)] hover:text-white transition-colors"
        >
          Restablecer valores Quantum
        </button>
      </div>
    </IndicatorConfigModal>
  )
}
